import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import i18n from '@/lib/i18n';
import type { Locale } from '@/types/domain';

interface LocaleState {
  locale: Locale;
  setLocale: (l: Locale) => void;
  toggle: () => void;
}

const LocaleCtx = createContext<LocaleState | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>((i18n.language as Locale) ?? 'th');

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<LocaleState>(
    () => ({
      locale,
      setLocale: (l) => {
        setLocaleState(l);
        void i18n.changeLanguage(l);
      },
      toggle: () => {
        const next: Locale = locale === 'en' ? 'th' : 'en';
        setLocaleState(next);
        void i18n.changeLanguage(next);
      },
    }),
    [locale],
  );

  return <LocaleCtx.Provider value={value}>{children}</LocaleCtx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLocale(): LocaleState {
  const ctx = useContext(LocaleCtx);
  if (!ctx) throw new Error('useLocale must be used within <LocaleProvider>');
  return ctx;
}
